import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Clock } from 'lucide-react';
import { Can } from '../../../shared/components/Can';
import { FEATURES } from '../../../shared/hooks/usePermissions';
import { ClienteForm } from '../components/ClienteForm';
import { ClienteDuplicadoModal } from '../components/ClienteDuplicadoModal';
import { ClienteSituacaoActions } from '../components/ClienteSituacaoActions';
import { ClienteEnderecos } from '../components/ClienteEnderecos';
import { ClienteContatos } from '../components/ClienteContatos';
import { ClienteFaturamento } from '../components/ClienteFaturamento';
import { ClientePerfilComercial } from '../components/ClientePerfilComercial';
import { ClienteTimeline } from '../components/ClienteTimeline';
import { ClienteValidacao } from '../components/ClienteValidacao';
import { ClienteVisao360 } from '../components/ClienteVisao360';
import { ClienteHistorico } from '../components/ClienteHistorico';
import { ClienteAcessosExternos } from '../components/ClienteAcessosExternos';
import { authService } from '../../../shared/lib/auth';
import {
  useAtualizarCliente,
  useAtualizarClienteDocumento,
  useCliente,
  usePendingChanges,
} from '../hooks';
import {
  ClienteDuplicadoError,
  SituacaoCadastral,
  type CreateClientePayload,
  type ExistingClienteRef,
  type UpdateClientePayload,
} from '../api';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';

const SITUACAO_LABEL: Record<SituacaoCadastral, string> = {
  em_cadastro: 'Em Cadastro',
  ativo: 'Ativo',
  inativo: 'Inativo',
  suspenso: 'Suspenso',
  bloqueado: 'Bloqueado',
  encerrado: 'Encerrado',
};

const SITUACAO_BADGE: Record<SituacaoCadastral, string> = {
  em_cadastro: 'bg-yellow-100 text-yellow-800',
  ativo: 'bg-green-100 text-green-800',
  inativo: 'bg-gray-100 text-gray-700',
  suspenso: 'bg-orange-100 text-orange-800',
  bloqueado: 'bg-red-100 text-red-800',
  encerrado: 'bg-gray-200 text-gray-800',
};

const ClienteDetalhePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: cliente, isLoading, isError, error } = useCliente(id);
  const atualizar = useAtualizarCliente();
  const atualizarDocumento = useAtualizarClienteDocumento();
  // SPEC 02 Fase D — mostra banner quando existem alterações críticas
  // aguardando aprovação para este cliente. Consulta só se tem id.
  const pendingChanges = usePendingChanges({ clientId: id, status: 'pending' });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [duplicado, setDuplicado] = useState<ExistingClienteRef | null>(null);

  // Mensagem vinda do fluxo de criação (ClientesPage.handleCreate).
  // Consome uma única vez: limpa o state pra não repetir em F5/back.
  useEffect(() => {
    const msg = (location.state as { successMessage?: string } | null)?.successMessage;
    if (!msg) return;
    setSuccessMessage(msg);
    navigate(location.pathname, { replace: true, state: null });
    const t = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const handleSubmit = async (payload: CreateClientePayload) => {
    if (!id || !cliente) return;
    setSaveError(null);
    const { tipoPessoa: _tipo, documento: novoDocumento, ...rest } = payload;
    const updatePayload: UpdateClientePayload = rest;
    const documentoMudou = isAdmin && novoDocumento && novoDocumento !== cliente.documento;

    try {
      if (documentoMudou) {
        await atualizarDocumento.mutateAsync({ id, documento: novoDocumento });
      }
      const res = await atualizar.mutateAsync({ id, data: updatePayload });
      // SPEC 02 Fase D — backend devolve `pendingChange` no response
      // quando algum campo crítico (razão social) foi enfileirado.
      const pending = (res as { pendingChange?: { fields: string[] } }).pendingChange;
      if (pending) {
        setSuccessMessage(
          `Alterações salvas. ${pending.fields.length === 1 ? 'O campo' : 'Os campos'} ${pending.fields.join(', ')} ${
            pending.fields.length === 1 ? 'aguarda' : 'aguardam'
          } aprovação (RN-COM-050).`,
        );
      } else {
        setSuccessMessage('Alterações salvas.');
      }
      setTimeout(() => setSuccessMessage(null), 6000);
    } catch (err) {
      if (err instanceof ClienteDuplicadoError) {
        setDuplicado(err.existingClient);
        return;
      }
      setSaveError(getFriendlyErrorMessage(err, 'Erro ao salvar alterações.'));
    }
  };

  if (isLoading) return <div className="p-6 text-sm text-gray-500">Carregando…</div>;
  if (isError) return (
    <div className="p-6 text-sm text-red-700">
      Não foi possível carregar o cliente. {getFriendlyErrorMessage(error, '')}
    </div>
  );
  if (!cliente) return null;

  const submitting = atualizar.isPending || atualizarDocumento.isPending;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/clientes')}
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
            aria-label="Voltar"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">Cliente {cliente.code}</div>
            <h1 className="text-2xl font-bold text-gray-900">{cliente.razaoSocial}</h1>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>Situação:</span>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SITUACAO_BADGE[cliente.situacaoCadastral]}`}>
                {SITUACAO_LABEL[cliente.situacaoCadastral]}
              </span>
              <span>·</span>
              <span>Criado em {new Date(cliente.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>
      </div>

      {cliente.situacaoCadastral === 'suspenso' && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0" />
          <div>
            <strong>Cliente suspenso.</strong> Operações são permitidas, mas confirme com a
            Governança antes de emitir novas propostas ou contratos.
          </div>
        </div>
      )}
      {(cliente.situacaoCadastral === 'bloqueado' || cliente.situacaoCadastral === 'encerrado') && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0" />
          <div>
            <strong>Cliente {SITUACAO_LABEL[cliente.situacaoCadastral].toLowerCase()}.</strong>{' '}
            Novas propostas, contratos e OS são bloqueados (RN-COM-006).
          </div>
        </div>
      )}

      {successMessage && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {(pendingChanges.data?.data.length ?? 0) > 0 && (
        <div
          className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-start gap-2"
          data-testid="pending-changes-banner"
        >
          <Clock size={18} className="flex-shrink-0" />
          <div className="flex-1">
            <strong>
              {pendingChanges.data!.data.length}{' '}
              {pendingChanges.data!.data.length === 1 ? 'alteração' : 'alterações'} crítica(s)
              aguardando aprovação
            </strong>{' '}
            — o valor atual continua vigente até aprovação.
            <Can feature={FEATURES.clientesAprovacoes} action="edit">
              <Link
                to="/clientes/aprovacoes-pendentes"
                className="ml-2 underline hover:no-underline"
              >
                Abrir fila de aprovações
              </Link>
            </Can>
          </div>
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Ciclo de vida</h2>
        {/*
          SPEC 02 Fase C — barra de completude + pendências fica junto do
          ciclo de vida porque impacta o botão "Ativar cliente". Clique em
          uma pendência faz scroll até a aba/campo responsável.
        */}
        <div className="mb-4">
          <ClienteValidacao clientId={cliente.id} />
        </div>
        <ClienteSituacaoActions clientId={cliente.id} situacao={cliente.situacaoCadastral} />
        {cliente.situacaoCadastral === 'em_cadastro' && (
          <p className="mt-3 text-xs text-gray-500">
            💡 Dica: alterações no formulário abaixo são salvas como <strong>rascunho</strong>{' '}
            enquanto o cliente estiver em "Em Cadastro". A ativação exige zero pendências acima.
          </p>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Dados cadastrais</h2>
        <ClienteForm
          mode="edit"
          canEditDocumento={isAdmin}
          initial={{
            tipoPessoa: cliente.tipoPessoa,
            razaoSocial: cliente.razaoSocial,
            nomeFantasia: cliente.nomeFantasia ?? undefined,
            documento: cliente.documento,
            inscricaoEstadual: cliente.inscricaoEstadual ?? undefined,
            inscricaoMunicipal: cliente.inscricaoMunicipal ?? undefined,
            dataFundacao: cliente.dataFundacao ?? undefined,
            cnaePrincipal: cliente.cnaePrincipal ?? undefined,
            segmentoMercado: cliente.segmentoMercado,
            porte: cliente.porte ?? undefined,
            classificacao: cliente.classificacao ?? undefined,
            origem: cliente.origem ?? undefined,
          }}
          submitting={submitting}
          submitError={saveError}
          onSubmit={handleSubmit}
        />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteEnderecos clientId={cliente.id} />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteContatos clientId={cliente.id} />
      </section>

      <Can feature={FEATURES.clientesAcessosExternos} action="view">
        <section className="rounded-lg border border-gray-200 bg-white p-6">
          <ClienteAcessosExternos clientId={cliente.id} />
        </section>
      </Can>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClientePerfilComercial clientId={cliente.id} />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteFaturamento clientId={cliente.id} />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteTimeline clientId={cliente.id} />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteVisao360 clientId={cliente.id} />
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <ClienteHistorico clientId={cliente.id} />
      </section>

      {duplicado && (
        <ClienteDuplicadoModal existingClient={duplicado} onClose={() => setDuplicado(null)} />
      )}
    </div>
  );
};

export default ClienteDetalhePage;
